import * as React from "react";
import { useEffect, useMemo, useRef, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams } from "ag-grid-enterprise";
import IconButton from "@mui/material/IconButton";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { STATE_SUMMARY_UPDATING } from "../../../constants/FinanceRecoil";
import { FilterDef } from "../../../constants/FinanceModels";
import {
    AddRounded,
    DeleteRounded,
    DescriptionRounded,
    DownloadRounded,
    InfoRounded,
    LockOpenRounded,
    LockRounded,
    LoopRounded,
    ScheduleSendRounded
} from "@mui/icons-material";
import { Button, CircularProgress, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { formatCurrency, formatDate } from "../../../utils/FormatUtils";
import { getLocalGridState, setLocalGridState } from "../../../utils/GridUtils";
import { STATE_PRINTOUT_ACTIVE_FILTERS, STATE_PRINTOUT_TOTAL_COUNT, STATE_PRINTOUT_UPDATING } from "../constants/MobilityStoreRecoil";
import ColouredChip from "../../../component/ColouredChip";
import { ID_BILLED, ID_ERROR, ID_PROCESSING, ID_PUBLISHED, ID_SENT, ID_TO_SEND } from "../constants/MobilityStoreStatuses";
import { COLOR_BILLED, COLOR_ERROR, COLOR_PUBLISHED, COLOR_SENT, COLOR_TO_SEND } from "../constants/MobilityStoreColors";
import { useMobilityStoreRepository } from "../../../api/repositories/MobilityStoreRepository";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import FinanceDialog from "../../../component/FinanceDialog";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import EmptyState from "../../../../../util/component/EmptyState";
import { useNavigate } from "react-router-dom";
import { NumericSummary } from "../../../component/NumericSummary";
import { useAgGridLocale, useHasScope } from "../../../../../config/globalConfig";
import { MS_AUTOMATIC_BILLING, MS_CREATE_PRINTOUTS, MS_DELETE_PRINTOUT, MS_DOWNLOAD_PRINTOUT, MS_REPROCESS_PRINTOUT, MS_SEND_IN_INVOICING_MANUALLY } from "../../../utils/FinanceScope";
import {downloadFilePdf} from "./ReportTableUtil";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import {decode} from "../../../utils/Base64Utils";


const PrintoutTable = () => {

    const UNIQUE_ID = "fnn-ms-printout";

    const { t } = useTranslation('finance', { keyPrefix: 'mobility-store' });
    const { getPrintouts, reprocessPrintout, deletePrintout, downloadPDF, switchPrintoutManualBilling, updatePrintoutStatus } = useMobilityStoreRepository();
    const hasScope = useHasScope;
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [reportToDelete, setReportToDelete] = useState({ id: '', msName: '', type: '' });
    const [openSendDialog, setOpenSendDialog] = useState(false);
    const [printoutToSend, setPrintoutToSend] = useState('');

    const [updating, setUpdating] = useRecoilState(STATE_PRINTOUT_UPDATING);
    const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);
    const navigate = useNavigate();
    const { getSummary, } = useMobilityStoreRepository();
    const agGridLocale = useAgGridLocale();

    const setTotalData = useSetRecoilState(STATE_PRINTOUT_TOTAL_COUNT)

    const filterDefs = useRecoilValue(STATE_PRINTOUT_ACTIVE_FILTERS);

    const theme = useTheme();
    const gridRef = useRef<AgGridReact>(null);

    const {  globalData: { user } } = useContext(GlobalContext);
        
    const legalEntities: any[] = user.legalEntities?.filter((x: any) => x.name.toLowerCase() !== "all");
    const dealers: any[] = user.dealers?.filter((x: any) => x.name.toLowerCase() !== "all");


    const columnDefs : ColDef[] = [
        {
            field: 'printoutRequest.name',
            headerName: t('tables.printout.report-name'),
            headerTooltip: t('tables.printout.report-name'),
            minWidth: 350,
            unSortIcon: true,
            pinned: "left",
            lockPosition: "left",
            lockVisible: true,
            lockPinned: true
        },
        {
            field: 'printoutRequest.created',
            headerName: t('tables.printout.report-creation-date'),
            headerTooltip: t('tables.printout.report-creation-date'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatDate(params.value)
        },
        {
            field: 'msCode',
            headerName: t('tables.printout.ms-code'),
            headerTooltip: t('tables.printout.ms-code'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'msName',
            headerName: t('tables.printout.ms-name'),
            headerTooltip: t('tables.printout.ms-name'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'msSapId',
            headerName: t('tables.printout.ms-sap-id'),
            headerTooltip: t('tables.printout.ms-sap-id'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'msVendorId',
            headerName: t('tables.printout.ms-vendor-id'),
            headerTooltip: t('tables.printout.ms-vendor-id'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'msVendorName',
            headerName: t('tables.printout.ms-vendor-name'),
            headerTooltip: t('tables.printout.ms-vendor-name'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'msVendorEmail',
            headerName: t('tables.printout.ms-vendor-email'),
            headerTooltip: t('tables.printout.ms-vendor-email'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'msType',
            headerName: t('tables.printout.ms-type'),
            headerTooltip: t('tables.printout.ms-type'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => params.value ? t('printout-ms-types.' + params.value) : '---'
        },
        {
            field: 'type',
            headerName: t('tables.printout.typology'),
            headerTooltip: t('tables.printout.typology'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => t('printout-types.' + params.value)
        },
        {
            field: 'clusterName',
            headerName: t('tables.printout.cluster-name'),
            headerTooltip: t('tables.printout.cluster-name'),
            minWidth: 200,
            valueFormatter: params => params.value || '---',
            unSortIcon: true
        },
        {
            field: 'clusterType',
            headerName: t('tables.printout.cluster-type'),
            headerTooltip: t('tables.printout.cluster-type'),
            cellRenderer: (params: any) => t(`tables.printout.cluster-types.${params.value}`),
            minWidth: 200,
            unSortIcon: true
        },
        {
            field: 'totalAmount',
            headerName: t('tables.printout.total-amount'),
            headerTooltip: t('tables.printout.total-amount'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatCurrency(params.value, 'EUR', 0.01)
        },
        {
            field: "invoice.invoiceNumber", headerName: t("tables.printout.invoiceNumber"),
            minWidth: 180,
            unSortIcon: true, sortable: true
        },
        {
            field: "invoice.sapInvoiceNumber", headerName: t("tables.printout.sapInvoiceNumber"),
            minWidth: 180,
            unSortIcon: true, sortable: true
        },
        {
            field: "legalEntity", headerName: t("tables.printout.legalEntity"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: legalEntities?.length <= 1
        },
        {
            field: "dealer", headerName: t("tables.printout.dealer"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: dealers?.length <= 1
        },
        {
            field: 'sentDate',
            headerName: t('tables.printout.published-date'),
            headerTooltip: t('tables.printout.published-date'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatDate(params.value)
        },
        {
            field: 'status', headerName: t('tables.printout.status'), headerTooltip: t('tables.printout.status'),
            minWidth: 150, unSortIcon: true,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                const status = params.data.status;
                switch (status) {
                    case ID_TO_SEND:
                        return {
                            component: () => <ColouredChip name={ID_TO_SEND} color={COLOR_TO_SEND}/>
                        };
                    case ID_PUBLISHED:
                        return {
                            component: () => <ColouredChip name={ID_PUBLISHED} color={COLOR_PUBLISHED}/>
                        };
                    case ID_SENT:
                        return {
                            component: () => <ColouredChip name={ID_SENT} color={COLOR_SENT}/>
                        };
                    case ID_BILLED:
                        return {
                            component: () => <ColouredChip name={ID_BILLED} color={COLOR_BILLED}/>
                        };
                    case ID_PROCESSING:
                        return {
                            component: () => (<Stack sx={{ alignItems: 'center', height: '100%' }} direction={'row'} spacing={1}>
                                        <Typography fontSize={'small'}>
                                            {t(`statuses.` + ID_PROCESSING)}
                                        </Typography>
                                        <CircularProgress size='15px' disableShrink={true}/>
                                    </Stack>
                            )
                        };
                    case ID_ERROR:
                        return {
                            component: () => <ColouredChip name={ID_ERROR} color={COLOR_ERROR}/>
                        };
                    default:
                        break;
                }
            }
        },
        {
            field: '_actions', headerName: '',
            suppressSizeToFit: true, minWidth: 100, maxWidth: 110, sortable: false,
            pinned: 'right', lockPosition: 'right', lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                return {
                    component: () =>
                            <Stack direction={"row"} justifyContent={"center"}>
                                {hasScope(MS_AUTOMATIC_BILLING) &&
                                <IconButton sx={{ color: theme.palette.accent.main }}
                                            disabled={params.data.status === ID_PROCESSING || params.data.status === ID_SENT || params.data.status === ID_BILLED}
                                            onClick={() => {
                                                switchPrintoutManualBilling(params.data.id).then(() => {
                                                    setSummaryUpdating(true);
                                                    setUpdating(true);
                                                });
                                            }}>
                                    <Tooltip title={t('tooltips.manual_billing.' + params.data.manualBilling)} arrow placement="left">
                                        {params.data.manualBilling ? <LockRounded/> : <LockOpenRounded/>}
                                    </Tooltip>
                                </IconButton>
                                }
                                {(hasScope(MS_DOWNLOAD_PRINTOUT) || hasScope(MS_REPROCESS_PRINTOUT) || hasScope(MS_SEND_IN_INVOICING_MANUALLY) || hasScope(MS_DELETE_PRINTOUT)) &&
                                <FinanceDynamicActions>
                                    {hasScope(MS_DOWNLOAD_PRINTOUT) &&
                                    <IconButton disabled={params.data.status === ID_PROCESSING || params.data.status === ID_ERROR} sx={{ color: theme.palette.accent.main }} onClick={() => {
                                        downloadPDF(params.data.id, "HTML").then((res) => {
                                            if (res?.result) {
                                                let pdfWindow = window.open("");
                                                pdfWindow!.document.write(decode(res.result));
                                            }
                                        });
                                    }}>
                                        <DescriptionRounded/>
                                    </IconButton>
                                    }
                                    {hasScope(MS_DOWNLOAD_PRINTOUT) &&
                                        <IconButton disabled={params.data.status === ID_PROCESSING || params.data.status === ID_ERROR} sx={{ color: theme.palette.accent.main }} onClick={() => {
                                            downloadPDF(params.data.id, "PDF").then((res) => {
                                                if (res?.result) {
                                                    downloadFilePdf(res?.result, "printout"+params.data.id+".pdf");
                                                }
                                            });
                                        }}>
                                            <DownloadRounded/>
                                        </IconButton>


                                    }
                                    {hasScope(MS_REPROCESS_PRINTOUT) &&
                                    <IconButton disabled={params.data.status !== ID_TO_SEND && params.data.status !== ID_PUBLISHED && params.data.status !== ID_ERROR} sx={{ color: theme.palette.accent.main }} onClick={() => {
                                        reprocessPrintout(params.data.id).then(() => {
                                            setUpdating(true);
                                            setSummaryUpdating(true);
                                        });
                                    }}>
                                        <LoopRounded/>
                                    </IconButton>
                                    }
                                    {hasScope(MS_SEND_IN_INVOICING_MANUALLY) &&
                                    <IconButton disabled={params.data.status !== ID_PUBLISHED} sx={{ color: theme.palette.accent.main }} onClick={() => {
                                        setOpenSendDialog(true);
                                        setPrintoutToSend(params.data.id)
                                    }}>
                                        <ScheduleSendRounded/>
                                    </IconButton>
                                    }
                                    {hasScope(MS_DELETE_PRINTOUT) &&
                                    <IconButton disabled={params.data.status === ID_SENT || params.data.status === ID_BILLED} sx={{ color: theme.palette.accent.main }} onClick={() => {
                                        setOpenDeleteDialog(true);
                                        setReportToDelete({
                                            id: params.data.id,
                                            msName: params.data.msName,
                                            type: t('printout-types.' + params.data.type)
                                        });
                                    }}>
                                        <DeleteRounded/>
                                    </IconButton>
                                    }
                                </FinanceDynamicActions>
                                }
                            </Stack>
                };
            },
            suppressFiltersToolPanel: true
        }
    ];

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: {
            title: t('tables.printout.overlays.no-rows.title'),
            content: t('tables.printout.overlays.no-rows.content')
        }
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
        setSummaryUpdating(true);
    }, [filterDefs])

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
            setUpdating(false);
            setSummaryUpdating(true);
        }
    }, [updating])

    useEffect(() => {
        setSummaryUpdating(true);
    }, [agGridLocale]);

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                let order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;

                getPrintouts(startRow, endRow - startRow, sort, order, filterDefs)
                        .then(response => {
                            let result = response.result;
                            setTotalData(result.page.totalElements);
                            if (result.page.totalElements === 0) {
                                api.showNoRowsOverlay();
                            } else {
                                api.hideOverlay();
                            }

                            params.success({
                                rowData: result.content,
                                rowCount: result.page.totalElements
                            })
                        })
                        .catch(() => params.fail());
            }
        };
    }


    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api?.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState()
        });
    }

    const toolbar = {
        renderInfo: () => (
                <NumericSummary query={() => getSummary(filterDefs)} fields={[ID_PROCESSING, ID_TO_SEND, ID_PUBLISHED, ID_SENT, ID_BILLED, ID_ERROR]} i18nPath={"mobility-store.counters.printouts"}/>
        ),
        renderActions: () => (
                <Stack>
                    {hasScope(MS_CREATE_PRINTOUTS) &&
                    <Button fullWidth variant="contained" component="label" onClick={() => {
                        navigate("/finance/mobility-store/printouts-wizard")
                    }} startIcon={<AddRounded/>}>{t('buttons.add-printout')}</Button>
                    }
                </Stack>
        )
    }

    return (
            <>
                <AgGridCustomTable
                        toolbar={toolbar}
                        noRowsOverlayComponent={noRowsOverlayComponent}
                        rowModelType='serverSide'
                        paginationPageSize={getLocalGridState(UNIQUE_ID).rows} serverSideInfiniteScroll
                        gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                        rowSelection={'multiple'}
                        suppressRowClickSelection
                        onGridReady={() => {
                            gridRef.current!.api.setServerSideDatasource(createDatasource(filterDefs, gridRef.current!.api));

                            let gridState = getLocalGridState(UNIQUE_ID).cols;
                            if (gridState !== undefined) {
                                gridRef.current!.columnApi.applyColumnState({
                                    state: gridState,
                                    applyOrder: true
                                })
                            }
                        }}
                        onColumnMoved={e => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onColumnPinned={e => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onColumnResized={e => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onSortChanged={e => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onPaginationChanged={() => {
                            setLocalGridState(UNIQUE_ID, {
                                rows: gridRef.current!.api?.paginationGetPageSize(),
                                cols: getLocalGridState(UNIQUE_ID).cols
                            });
                        }}
                >
                </AgGridCustomTable>
                <FinanceDialog title={t('tables.printout.delete-dialog.title')}
                               content={t('tables.printout.delete-dialog.content', { msName: reportToDelete.msName, type: reportToDelete.type })}
                               cancelButton={t('tables.printout.delete-dialog.cancel')}
                               confirmButton={t('tables.printout.delete-dialog.confirm')}
                               open={openDeleteDialog}
                               titleIcon={<InfoRounded sx={{ color: 'accent.main' }}></InfoRounded>}
                               callback={() => {
                                   deletePrintout(reportToDelete.id).then(() => setUpdating(true))
                               }}
                               onClose={() => {
                                   setOpenDeleteDialog(false)
                               }}
                />
                <FinanceDialog title={t('tables.printout.send-dialog.title')}
                               content={t('tables.printout.send-dialog.content')}
                               cancelButton={t('tables.printout.send-dialog.cancel')}
                               confirmButton={t('tables.printout.send-dialog.confirm')}
                               open={openSendDialog}
                               titleIcon={<InfoRounded sx={{ color: 'accent.main' }}></InfoRounded>}
                               callback={() => {
                                   updatePrintoutStatus(printoutToSend).then((res) => {
                                       setUpdating(true);
                                       setSummaryUpdating(true);
                                   })
                               }}
                               onClose={() => {
                                   setOpenSendDialog(false)
                               }}
                />
            </>
    );
}

export default PrintoutTable;